import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Upload, FileText, Sparkles, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'client',         label: 'Vente (Client)',         color: 'bg-emerald-100 text-emerald-700' },
  { key: 'fournisseur',    label: 'Achat (Fournisseur)',    color: 'bg-orange-100 text-orange-700' },
  { key: 'investissement', label: 'Investissement',          color: 'bg-purple-100 text-purple-700' },
  { key: 'impot',          label: 'Impôt / Taxe',           color: 'bg-rose-100 text-rose-700' },
];

const emptyDraft = () => ({
  numero: '',
  date_facture: new Date().toISOString().slice(0, 10),
  date_echeance: '',
  client: { nom: '', adresse: '', nif: '', email: '', telephone: '' },
  objet: '',
  lignes: [],
  total_ht: 0,
  tva_pct: 19,
  tva_montant: 0,
  total_ttc: 0,
  devise: 'FCFA',
  categorie: 'client',
  confidence: 0,
  raison_categorie: '',
});

export default function ImportFacturePDFDialog({ open, onOpenChange, onImported }) {
  const [stage, setStage] = useState('select'); // select | analyzing | review
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStage('select');
    setFile(null);
    setDraft(emptyDraft());
    setSaving(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Merci de sélectionner un PDF');
      return;
    }
    setFile(f);
    setStage('analyzing');
    try {
      // 1) Convertir le fichier en base64 (pour le conserver comme aperçu original)
      const sourcePdfB64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      // 2) Analyser via Gemini
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(`${API}/api/factures/import-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.success && data.draft) {
        setDraft({
          ...emptyDraft(),
          ...data.draft,
          source_pdf_b64: sourcePdfB64,
          source_pdf_filename: f.name,
        });
        setStage('review');
        toast.success('Facture analysée par Gemini 3 Pro');
      } else {
        throw new Error('Réponse IA invalide');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Échec analyse PDF';
      toast.error(msg);
      setStage('select');
      setFile(null);
    }
  };

  const updateClient = (key, val) => setDraft(d => ({ ...d, client: { ...d.client, [key]: val } }));
  const updateField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      delete payload.confidence;
      delete payload.raison_categorie;
      delete payload.created_by_hint;
      await axios.post(`${API}/api/factures`, payload);
      toast.success('Facture importée et enregistrée');
      onImported?.();
      close();
    } catch (e) {
      toast.error('Erreur enregistrement : ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const confidenceBadge = () => {
    const c = Number(draft.confidence || 0);
    if (c >= 0.85) return { label: 'Confiance haute', cls: 'bg-emerald-100 text-emerald-700' };
    if (c >= 0.6)  return { label: 'Confiance moyenne', cls: 'bg-amber-100 text-amber-700' };
    return { label: 'À vérifier', cls: 'bg-rose-100 text-rose-700' };
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-pdf-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            Importer une facture PDF
          </DialogTitle>
          <DialogDescription>
            Gemini 3 Pro lit ton PDF, extrait les données et classe automatiquement la catégorie.
          </DialogDescription>
        </DialogHeader>

        {stage === 'select' && (
          <label
            className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-emerald-300 rounded-xl p-10 cursor-pointer hover:bg-emerald-50 transition"
            data-testid="dropzone-pdf"
          >
            <Upload className="h-10 w-10 text-emerald-600" />
            <p className="font-semibold text-slate-700">Cliquer ou glisser-déposer un PDF</p>
            <p className="text-xs text-slate-500">15 Mo maximum</p>
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="input-pdf"
            />
          </label>
        )}

        {stage === 'analyzing' && (
          <div className="flex flex-col items-center justify-center gap-3 p-10" data-testid="state-analyzing">
            <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
            <p className="font-semibold text-slate-700">Analyse en cours…</p>
            <p className="text-xs text-slate-500">{file?.name}</p>
            <p className="text-xs text-slate-400">Gemini 3 Pro extrait les champs et la catégorie</p>
          </div>
        )}

        {stage === 'review' && (
          <div className="space-y-4" data-testid="state-review">
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-3 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700">{file?.name}</p>
                  <p className="text-xs text-slate-600 italic">{draft.raison_categorie || 'Catégorie suggérée par l\'IA'}</p>
                </div>
                <Badge className={confidenceBadge().cls}>{confidenceBadge().label}</Badge>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="imp-numero" className="text-xs">N° Facture</Label>
                <Input id="imp-numero" value={draft.numero || ''} onChange={(e) => updateField('numero', e.target.value)} data-testid="imp-numero" />
              </div>
              <div>
                <Label htmlFor="imp-cat" className="text-xs">Catégorie auto-détectée</Label>
                <Select value={draft.categorie} onValueChange={(v) => updateField('categorie', v)}>
                  <SelectTrigger id="imp-cat" data-testid="imp-categorie"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="imp-date" className="text-xs">Date facture</Label>
                <Input id="imp-date" type="date" value={draft.date_facture || ''} onChange={(e) => updateField('date_facture', e.target.value)} data-testid="imp-date" />
              </div>
              <div>
                <Label htmlFor="imp-ech" className="text-xs">Échéance</Label>
                <Input id="imp-ech" type="date" value={draft.date_echeance || ''} onChange={(e) => updateField('date_echeance', e.target.value)} data-testid="imp-echeance" />
              </div>
            </div>

            <fieldset className="border rounded-lg p-3 space-y-2">
              <legend className="px-2 text-xs font-semibold text-slate-600">Contre-partie</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom / Raison sociale</Label>
                  <Input value={draft.client?.nom || ''} onChange={(e) => updateClient('nom', e.target.value)} data-testid="imp-client-nom" />
                </div>
                <div>
                  <Label className="text-xs">NIF</Label>
                  <Input value={draft.client?.nif || ''} onChange={(e) => updateClient('nif', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Adresse</Label>
                  <Input value={draft.client?.adresse || ''} onChange={(e) => updateClient('adresse', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={draft.client?.email || ''} onChange={(e) => updateClient('email', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Téléphone</Label>
                  <Input value={draft.client?.telephone || ''} onChange={(e) => updateClient('telephone', e.target.value)} />
                </div>
              </div>
            </fieldset>

            <div>
              <Label htmlFor="imp-objet" className="text-xs">Objet</Label>
              <Input id="imp-objet" value={draft.objet || ''} onChange={(e) => updateField('objet', e.target.value)} data-testid="imp-objet" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Total HT</Label>
                <Input type="number" value={draft.total_ht || 0} onChange={(e) => updateField('total_ht', Number(e.target.value))} data-testid="imp-total-ht" />
              </div>
              <div>
                <Label className="text-xs">TVA %</Label>
                <Input type="number" value={draft.tva_pct || 0} onChange={(e) => updateField('tva_pct', Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-xs">Montant TVA</Label>
                <Input type="number" value={draft.tva_montant || 0} onChange={(e) => updateField('tva_montant', Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-xs">Total TTC</Label>
                <Input type="number" value={draft.total_ttc || 0} onChange={(e) => updateField('total_ttc', Number(e.target.value))} data-testid="imp-total-ttc" />
              </div>
            </div>

            {Array.isArray(draft.lignes) && draft.lignes.length > 0 && (
              <fieldset className="border rounded-lg p-3">
                <legend className="px-2 text-xs font-semibold text-slate-600">Lignes ({draft.lignes.length})</legend>
                <div className="space-y-1 text-xs">
                  {draft.lignes.map((ln, i) => (
                    <div key={i} className="flex justify-between gap-2 border-b last:border-b-0 py-1">
                      <span className="flex-1 truncate">{ln.designation || '—'}</span>
                      <span className="text-slate-500">{ln.quantite} × {Number(ln.prix_unitaire_ht).toLocaleString('fr-FR')} = <strong>{Number(ln.montant_ht).toLocaleString('fr-FR')}</strong></span>
                    </div>
                  ))}
                </div>
              </fieldset>
            )}

            {Number(draft.confidence || 0) < 0.6 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-3 flex items-start gap-2 text-xs text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>La confiance d'extraction est faible. Merci de relire attentivement les montants et la catégorie avant d'enregistrer.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          {stage === 'review' && (
            <>
              <Button variant="outline" onClick={() => { setStage('select'); setFile(null); }} data-testid="btn-reanalyser">
                <FileText className="h-4 w-4 mr-1" /> Autre PDF
              </Button>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-import">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Enregistrer la facture
              </Button>
            </>
          )}
          {stage !== 'review' && (
            <Button variant="outline" onClick={close}>Annuler</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
